import { ApiErrorCode, createApiError } from '@/modules/common/api/errors';
import {
  mapIntegrationProviderToDTO,
  mapSelectedIntegrationFromDTO,
} from '@/modules/common/mappers/integration.mapper';
import { IntegrationInfoResponse } from '@/modules/common/types/dto/response/integration-response';
import { EntityIntegration } from '@/modules/common/types/entity-integration';
import { IntegrationServices } from '@/modules/common/types/integrations';
import { getIntegrationIcon } from '@/modules/common/utils/integration-icons';
import { PatchProjectIntegrationsRequest } from '@/modules/projects/types/dto/request/update-project-request';

import { CreateIntegrationInfoRequest } from '../types/dto/request/create-project-request';
import type { ProjectIntegration } from '../types/project-integration';

/**
 * Converts generic EntityIntegration to ProjectIntegration.
 * Maps `entities` to `projects` and `selectedEntity` to `selectedValue`.
 * Sets allProjectsLinked=true when the integration has no available projects and no
 * selected value, meaning all external projects are already linked to other projects.
 */
export const mapEntityIntegrationToProjectIntegration = (
  entity: EntityIntegration
): ProjectIntegration => {
  const projects = entity.entities;
  const selectedValue = entity.selectedEntity ?? null;
  const allProjectsLinked = Array.isArray(projects) && projects.length === 0 && !selectedValue;

  return {
    id: entity.id,
    type: entity.type,
    icon: entity.icon,
    projects,
    selectedValue,
    allProjectsLinked,
  };
};

/**
 * Converts array of EntityIntegration to ProjectIntegration array.
 */
export const mapEntityIntegrationsToProjectIntegrations = (
  entities: EntityIntegration[]
): ProjectIntegration[] => {
  return entities.map(mapEntityIntegrationToProjectIntegration);
};

/**
 * Maps ProjectIntegration array to CreateIntegrationInfoRequest array.
 * Only includes integrations with selected projects.
 */
export const mapProjectIntegrationsToDTO = (
  integrations?: ProjectIntegration[]
): CreateIntegrationInfoRequest[] | undefined => {
  if (!integrations || integrations.length === 0) return undefined;

  return integrations
    .filter(integration => integration.selectedValue)
    .filter(integration => Number.isFinite(Number(integration.selectedValue?.id)))
    .map(integration => {
      const serviceType = integration.type as IntegrationServices;

      return {
        integrationProviderType: mapIntegrationProviderToDTO(serviceType),
        connectToLinkId: Number(integration.selectedValue?.id),
      };
    });
};

/**
 * Maps ProjectIntegration array to PatchProjectIntegrationsRequest.
 * Used for PATCH /api/projects/integrations endpoint.
 */
export const mapProjectIntegrationsToPatchRequest = (
  projectId: string | number,
  integrations?: ProjectIntegration[]
): PatchProjectIntegrationsRequest => {
  const id = typeof projectId === 'number' ? projectId : Number(projectId);

  if (!Number.isFinite(id)) {
    const originalError = new Error(
      `Invalid projectId provided to mapProjectIntegrationsToPatchRequest: ${projectId}`
    );
    throw createApiError(
      ApiErrorCode.PROJECT_UPDATE_FAILED,
      originalError,
      'Invalid project identifier for integrations update'
    );
  }

  const integrationsDTO = mapProjectIntegrationsToDTO(integrations);

  return {
    projectId: id,
    integrations: {
      integrations: integrationsDTO || [],
    },
  };
};

/**
 * Maps integration info responses from API to project integrations.
 * Converts DTO to domain model.
 */
export const mapIntegrationsFromResponse = (
  rawIntegrations: IntegrationInfoResponse[]
): ProjectIntegration[] => {
  return rawIntegrations.map(integration => {
    const { type, selectedEntity } = mapSelectedIntegrationFromDTO(integration);

    const entityIntegration: EntityIntegration = {
      id: type,
      type,
      icon: getIntegrationIcon(type as IntegrationServices),
      entities: null,
      selectedEntity,
    };

    return mapEntityIntegrationToProjectIntegration(entityIntegration);
  });
};
